/**
 * TanStack Query hooks for API calls
 */

import {
  useQuery,
  useMutation,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { metadataAPI, suggestionsAPI, getAPIForDomain } from './endpoints';
import { DomainType, SuggestionRequest } from '../types';

// Query keys
export const queryKeys = {
  metadata: ['metadata'] as const,
  organizations: (domain: DomainType) => ['organizations', domain] as const,
  terms: (domain: DomainType, aio?: boolean) => ['terms', domain, aio ?? false] as const,
  mapData: (domain: DomainType, term: string) => ['mapData', domain, term] as const,
  citySearchResults: (domain: DomainType, term: string, city: string) =>
    ['citySearchResults', domain, term, city] as const,
  rankings: (domain: DomainType, field?: string) => ['rankings', domain, field] as const,
  bubbleData: (domain: DomainType, field: string) => ['bubbleData', domain, field] as const,
  aioOverview: (domain: DomainType) => ['aioOverview', domain] as const,
  aioRankings: (domain: DomainType, field?: string, limit?: number) => ['aioRankings', domain, field ?? 'All', limit ?? 10] as const,
  aioBubbleData: (domain: DomainType, field?: string) => ['aioBubbleData', domain, field ?? 'All'] as const,
  aioMapData: (domain: DomainType, term: string) => ['aioMapData', domain, term] as const,
  aioCitySearchResults: (domain: DomainType, term: string, city: string) =>
    ['aioCitySearchResults', domain, term, city] as const,
  compare: (domain: DomainType, orgIds: number[]) => ['compare', domain, orgIds] as const,
  actorOverview: (domain: DomainType, orgId: number, useAIO = false) => ['actorOverview', domain, orgId, useAIO] as const,
  actorFields: (domain: DomainType, orgId: number, useAIO = false) =>
    ['actorFields', domain, orgId, useAIO] as const,
  actorFieldDetail: (domain: DomainType, orgId: number, field: string, useAIO = false) =>
    ['actorFieldDetail', domain, orgId, field, useAIO] as const,
  termByCity: (domain: DomainType, orgId: number, term: string, useAIO = false) =>
    ['termByCity', domain, orgId, term, useAIO] as const,
  comparisonGraph: (domain: DomainType, orgId1: number, orgId2: number, useAIO = false) =>
    ['comparisonGraph', domain, orgId1, orgId2, useAIO] as const,
  actorDetail: (domain: DomainType, orgId: number, field?: string, term?: string, useAIO = false) =>
    ['actorDetail', domain, orgId, field ?? '', term ?? '', useAIO] as const,
  orgIdsWithData: (domain: DomainType, useAIO = false) => ['orgIdsWithData', domain, useAIO] as const,
};

// Metadata
export const useMetadata = () => {
  return useQuery({
    queryKey: queryKeys.metadata,
    queryFn: async () => {
      const response = await metadataAPI.getMetadata();
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Organizations
export const useOrganizations = (domain: DomainType) => {
  return useQuery({
    queryKey: queryKeys.organizations(domain),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getOrganizations();
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Terms
export const useTerms = (domain: DomainType, aioTotals = false) => {
  return useQuery({
    queryKey: queryKeys.terms(domain, aioTotals),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getTerms(aioTotals);
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Map data
export const useMapData = (domain: DomainType, term: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.mapData(domain, term),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getMapData(term);
      return response.data;
    },
    enabled: enabled && !!term,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useCitySearchResults = (
  domain: DomainType,
  term: string,
  city: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: queryKeys.citySearchResults(domain, term, city),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getCitySearchResults(term, city);
      const list = response.data;
      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    },
    enabled: !!term && !!city && enabled,
  });
};

// Rankings
export const useRankings = (
  domain: DomainType,
  field?: string,
  limit?: number,
  enabled = true
) => {
  return useQuery({
    queryKey: queryKeys.rankings(domain, field),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getRankings(field, limit);
      return response.data;
    },
    enabled: enabled && !!domain,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

// Bubble data
export const useBubbleData = (domain: DomainType, field: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.bubbleData(domain, field),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getBubbleData(field);
      return response.data;
    },
    enabled: enabled && !!field,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useAIOOverview = (domain: DomainType, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.aioOverview(domain),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getAIOOverview();
      return response.data;
    },
    enabled: !!domain && enabled,
    staleTime: 1000 * 60 * 10,
  });
};

export const useAIORankings = (domain: DomainType, field = 'All', limit = 10, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.aioRankings(domain, field, limit),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getAIORankings(field, limit);
      return response.data;
    },
    enabled: !!domain && enabled,
    staleTime: 1000 * 60 * 10,
  });
};

export const useAIOBubbleData = (domain: DomainType, field = 'All', enabled = true) => {
  return useQuery({
    queryKey: queryKeys.aioBubbleData(domain, field),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getAIOBubbleData(field);
      return response.data;
    },
    enabled: !!domain && enabled,
    staleTime: 1000 * 60 * 10,
  });
};

export const useAIOMapData = (domain: DomainType, term: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.aioMapData(domain, term),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getAIOMapData(term);
      return response.data;
    },
    enabled: enabled && !!term,
    staleTime: 1000 * 60 * 10,
  });
};

export const useAIOCitySearchResults = (
  domain: DomainType,
  term: string,
  city: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: queryKeys.aioCitySearchResults(domain, term, city),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getAIOCitySearchResults(term, city);
      const list = response.data;
      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    },
    enabled: !!term && !!city && enabled,
    staleTime: 1000 * 60 * 10,
  });
};

// Compare organizations
export const useCompare = (domain: DomainType, orgIds: number[], enabled = true) => {
  return useQuery({
    queryKey: queryKeys.compare(domain, orgIds),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.compareOrganizations(orgIds);
      return response.data;
    },
    enabled: enabled && orgIds.length > 0,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

// Actor Analysis – org IDs that have any statistics (for filtering dropdowns)
function useOrgIdsWithData(domain: DomainType, enabled = true, useAIO = false) {
  return useQuery({
    queryKey: queryKeys.orgIdsWithData(domain, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getOrgIdsWithData(useAIO);
      return response.data;
    },
    enabled: !!domain && enabled,
    staleTime: 1000 * 60 * 5,
  });
}

function useActorOverview(domain: DomainType, orgId: number, enabled = true, useAIO = false) {
  return useQuery({
    queryKey: queryKeys.actorOverview(domain, orgId, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getActorOverview(orgId, useAIO);
      return response.data;
    },
    enabled: enabled && Number.isFinite(orgId) && orgId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

function useActorFields(domain: DomainType, orgId: number, enabled = true, useAIO = false) {
  return useQuery({
    queryKey: queryKeys.actorFields(domain, orgId, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getActorFields(orgId, useAIO);
      return response.data;
    },
    enabled: enabled && Number.isFinite(orgId) && orgId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

function useActorFieldDetail(
  domain: DomainType,
  orgId: number,
  field: string,
  enabled = true,
  useAIO = false
) {
  return useQuery({
    queryKey: queryKeys.actorFieldDetail(domain, orgId, field, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getActorFieldDetail(orgId, field, useAIO);
      return response.data;
    },
    enabled: enabled && Number.isFinite(orgId) && orgId > 0 && !!field,
    staleTime: 1000 * 60 * 5,
  });
}

function useTermByCity(
  domain: DomainType,
  orgId: number,
  term: string,
  enabled = true,
  useAIO = false
) {
  return useQuery({
    queryKey: queryKeys.termByCity(domain, orgId, term, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getTermByCity(orgId, term, useAIO);
      return response.data;
    },
    enabled: enabled && !!orgId && !!term,
    staleTime: 1000 * 60 * 5,
  });
}

function useComparisonGraph(
  domain: DomainType,
  orgId1: number,
  orgId2: number,
  enabled = true,
  useAIO = false
) {
  return useQuery({
    queryKey: queryKeys.comparisonGraph(domain, orgId1, orgId2, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getComparisonGraph(orgId1, orgId2, useAIO);
      return response.data;
    },
    enabled: enabled && !!orgId1 && !!orgId2,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

function useActorDetail(
  domain: DomainType,
  orgId: number,
  field?: string,
  term?: string,
  enabled = true,
  useAIO = false
) {
  return useQuery({
    queryKey: queryKeys.actorDetail(domain, orgId, field, term, useAIO),
    queryFn: async () => {
      const api = getAPIForDomain(domain);
      const response = await api.getActorDetail(orgId, field, term, useAIO);
      return response.data;
    },
    enabled: enabled && !!orgId,
    staleTime: 1000 * 60 * 5,
  });
}

/** Prefetch overview + fields (e.g. on dropdown hover) so selection feels instant. */
export async function prefetchActorData(
  queryClient: QueryClient,
  domain: DomainType,
  orgId: number,
  useAIO = false
) {
  const api = getAPIForDomain(domain);
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: queryKeys.actorOverview(domain, orgId, useAIO),
      queryFn: async () => (await api.getActorOverview(orgId, useAIO)).data,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.actorFields(domain, orgId, useAIO),
      queryFn: async () => (await api.getActorFields(orgId, useAIO)).data,
    }),
  ]);
}

export {
  useOrgIdsWithData,
  useActorOverview,
  useActorFields,
  useActorFieldDetail,
  useTermByCity,
  useActorDetail,
  useComparisonGraph,
};

// Mutations
export const useSuggestion = () => {
  return useMutation({
    mutationFn: async (data: SuggestionRequest) => {
      const response = await suggestionsAPI.submit(data);
      return response.data;
    },
  });
};
